// Step 11 — 1200×630 share card composition (main-thread Canvas).
//
// Layout (landscape 1.91:1 for FB/LINE/Twitter large image preview):
//
//   ┌──────────────────────────────────────────────┐  1200
//   │ ┌─────────┐                                  │
//   │ │         │  彩衣                            │
//   │ │         │  The Coat of Many Colors         │
//   │ │  hero   │  創 37:3                         │   630
//   │ │  art    │  雅各為約瑟特別做的彩色衣裳…   │
//   │ │         │                                  │
//   │ │         │  Alice 上色 · 2026/05/11         │
//   │ └─────────┘  台北靈糧堂      skena.tp.org   │
//   └──────────────────────────────────────────────┘
//
// Square 1:1 (the previous 1080×1080) was rejected by FB/LINE as ineligible
// for large-image preview; both platforms gate it on the 1.91:1 ratio.
//
// Tokens mirror docs/DESIGN.md by const, not import — see DESIGN.md "公開頁
// token sync" reminder. Same convention as functions/share/[id].ts.

const W = 1200
const H = 630
const HERO_PAD = 30
const HERO_SIZE = H - HERO_PAD * 2 // 570 — square hero region
const HERO_X = HERO_PAD
const HERO_Y = HERO_PAD
const TEXT_X = HERO_X + HERO_SIZE + 40 // 640
const TEXT_RIGHT_PAD = 40
const TEXT_W = W - TEXT_X - TEXT_RIGHT_PAD // 520

const COLORS = {
  parchment: '#f1e8d3',
  walnut: '#3a2818',
  dusty: '#a8957c',
  accentGold: '#c89a44',
}

const FONT_SERIF = `'Noto Serif TC', 'Songti TC', Georgia, serif`
const FONT_SANS = `'PingFang TC', 'Noto Sans TC', 'Microsoft JhengHei', system-ui, sans-serif`

// Defaults preserved for unit tests / external callers. The landscape layout
// uses LANDSCAPE_BUDGET / LANDSCAPE_MAX_LINES explicitly below.
const DESCRIPTION_BUDGET = 30
const DESCRIPTION_MAX_LINES = 2
const LANDSCAPE_BUDGET = 36
const LANDSCAPE_MAX_LINES = 3

export interface ShareCardInput {
  /** Loaded HTMLImageElement of the user's artwork (must be CORS-clean). */
  heroImg: HTMLImageElement
  itemNameZh: string
  itemNameEn: string
  scriptureRef: string
  descriptionZh: string
  displayName: string
  /** ISO date or any Date — used for "上色 · YYYY/MM/DD" credit. */
  date: Date
  /** Nullable: when null, the church-name line is omitted. */
  churchName: string | null
  /**
   * Step 13: true for bonus / capstone artworks. Adds the "✦ 全景大獎" badge
   * in the top-right of the card. Omitted/false → byte-for-byte identical to
   * the pre-Step-13 layout (regression-protected by a hash test).
   */
  isCapstone?: boolean
}

export class ShareCardCompositionError extends Error {
  constructor(
    public readonly reason: 'canvas-tainted' | 'toblob-failed' | 'unknown',
    message: string,
  ) {
    super(message)
  }
}

/**
 * Compose the 1200×630 share card. Returns a PNG Blob ready for R2 PUT.
 *
 * Throws ShareCardCompositionError with `reason='canvas-tainted'` when the
 * hero image's R2 origin lacked CORS — the caller should map this to the
 * user-visible toast "分享卡片準備失敗，請重試".
 */
export async function composeShareCard(input: ShareCardInput): Promise<Blob> {
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new ShareCardCompositionError('unknown', 'no 2d context')
  }

  // 1. Background — full parchment.
  ctx.fillStyle = COLORS.parchment
  ctx.fillRect(0, 0, W, H)

  // 2. Hero — square contain into left HERO_SIZE × HERO_SIZE region.
  drawContain(ctx, input.heroImg, HERO_X, HERO_Y, HERO_SIZE, HERO_SIZE)

  // 3. Vertical divider between hero & text column.
  ctx.fillStyle = COLORS.dusty
  ctx.fillRect(HERO_X + HERO_SIZE + 20, HERO_Y + 40, 1, HERO_SIZE - 80)

  // 4. Text block (right column).
  ctx.textBaseline = 'alphabetic'
  ctx.textAlign = 'left'

  // Title (serif 600 walnut).
  let y = 90
  ctx.fillStyle = COLORS.walnut
  ctx.font = `600 52px ${FONT_SERIF}`
  ctx.fillText(input.itemNameZh, TEXT_X, y)

  // English subtitle (italic dusty).
  if (input.itemNameEn) {
    ctx.fillStyle = COLORS.dusty
    ctx.font = `italic 400 24px ${FONT_SERIF}`
    ctx.fillText(input.itemNameEn, TEXT_X, y + 34)
    y += 34
  }

  y += 54

  // Scripture ref (serif gold).
  if (input.scriptureRef) {
    ctx.fillStyle = COLORS.accentGold
    ctx.font = `400 26px ${FONT_SERIF}`
    ctx.fillText(input.scriptureRef, TEXT_X, y)
    y += 42
  }

  // Description (sans walnut, wrapped + truncated to 36 chars / 3 lines).
  if (input.descriptionZh) {
    ctx.fillStyle = COLORS.walnut
    ctx.font = `400 24px ${FONT_SANS}`
    const lines = wrapAndTruncateChinese(
      ctx,
      input.descriptionZh,
      TEXT_W,
      LANDSCAPE_BUDGET,
      LANDSCAPE_MAX_LINES,
    )
    for (const line of lines) {
      ctx.fillText(line, TEXT_X, y)
      y += 34
    }
    y += 14
  }

  // Credit (sans walnut 500).
  ctx.fillStyle = COLORS.walnut
  ctx.font = `500 22px ${FONT_SANS}`
  const dateStr = formatDate(input.date)
  ctx.fillText(`${input.displayName} 上色 · ${dateStr}`, TEXT_X, y)
  y += 30

  // Church name (sans dusty 400) — null-safe.
  if (input.churchName) {
    ctx.fillStyle = COLORS.dusty
    ctx.font = `400 20px ${FONT_SANS}`
    ctx.fillText(input.churchName, TEXT_X, y)
  }

  // Watermark — bottom-right (serif 600 gold).
  ctx.fillStyle = COLORS.accentGold
  ctx.font = `600 20px ${FONT_SERIF}`
  ctx.textAlign = 'right'
  ctx.fillText('skena.trailpaint.org', W - TEXT_RIGHT_PAD, H - 28)

  // Step 13: capstone badge — top-right gold pill with "✦ 全景大獎". Drawn
  // last so it sits on top of any earlier layer. When isCapstone is
  // omitted/false the canvas is byte-identical to the pre-Step-13 layout.
  if (input.isCapstone) {
    drawCapstoneBadge(ctx)
  }

  return await canvasToBlob(canvas)
}

/** Capstone "全景大獎" badge — accent-gold rounded pill, serif walnut text. */
export function drawCapstoneBadge(ctx: CanvasRenderingContext2D): void {
  const badgeW = 200
  const badgeH = 60
  const badgeX = W - badgeW - 30
  const badgeY = 30
  const r = 30

  // Rounded-pill background.
  ctx.fillStyle = COLORS.accentGold
  ctx.beginPath()
  // Use the standardized roundRect when available, otherwise fall back to
  // arcs. roundRect lands in Chromium 99+ / Safari 16+ — well within target.
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(badgeX, badgeY, badgeW, badgeH, r)
  } else {
    ctx.moveTo(badgeX + r, badgeY)
    ctx.arcTo(badgeX + badgeW, badgeY, badgeX + badgeW, badgeY + badgeH, r)
    ctx.arcTo(badgeX + badgeW, badgeY + badgeH, badgeX, badgeY + badgeH, r)
    ctx.arcTo(badgeX, badgeY + badgeH, badgeX, badgeY, r)
    ctx.arcTo(badgeX, badgeY, badgeX + badgeW, badgeY, r)
    ctx.closePath()
  }
  ctx.fill()

  // Centered serif walnut label. Save/restore so we don't leak textAlign.
  ctx.save()
  ctx.fillStyle = COLORS.walnut
  ctx.font = `600 24px ${FONT_SERIF}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('✦ 全景大獎', badgeX + badgeW / 2, badgeY + badgeH / 2 + 1)
  ctx.restore()
}

/**
 * Wrap Chinese text by character width measurement, truncate at `charsBudget`
 * adding `…`, and never exceed `maxLines`.
 *
 * Sizing math (Decision 9, design.md): bottom band = 1016px after 32px x2
 * padding; with 30px sans, Chinese chars are ~30px wide → ~33 chars/line; 2
 * lines = ~66 chars, well above the 30-char budget. Wrap rarely triggers
 * mid-budget; truncation is the load-bearing rule.
 */
export function wrapAndTruncateChinese(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  charsBudget = DESCRIPTION_BUDGET,
  maxLines = DESCRIPTION_MAX_LINES,
): string[] {
  const trimmed = [...text].slice(0, charsBudget + 1).join('')
  const truncated = [...text].length > charsBudget
  const baseChars = truncated
    ? [...text].slice(0, charsBudget - 1).join('') + '…'
    : trimmed

  const chars = [...baseChars]
  const lines: string[] = []
  let cur = ''
  for (const ch of chars) {
    const next = cur + ch
    if (ctx.measureText(next).width > maxWidth && cur.length > 0) {
      lines.push(cur)
      if (lines.length >= maxLines) {
        // Reached line cap mid-budget — collapse the tail into the last line
        // with an ellipsis. Should be rare given the sizing math above.
        const last = lines.pop() ?? ''
        const folded = (last + ch).slice(0, -1) + '…'
        lines.push(folded)
        return lines
      }
      cur = ch
    } else {
      cur = next
    }
  }
  if (cur) lines.push(cur)
  return lines
}

function drawContain(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const iw = img.naturalWidth || img.width
  const ih = img.naturalHeight || img.height
  if (!iw || !ih) return
  const ratio = Math.min(w / iw, h / ih)
  const dw = iw * ratio
  const dh = ih * ratio
  const dx = x + (w - dw) / 2
  const dy = y + (h - dh) / 2
  try {
    ctx.drawImage(img, dx, dy, dw, dh)
  } catch (err) {
    // drawImage itself throws synchronously when the image is tainted; the
    // toBlob downstream would also reject. Surface the typed error.
    throw new ShareCardCompositionError(
      'canvas-tainted',
      `drawImage failed (likely missing CORS on hero): ${err instanceof Error ? err.message : String(err)}`,
    )
  }
}

function formatDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}/${m}/${day}`
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new ShareCardCompositionError('toblob-failed', 'canvas.toBlob returned null'))
        return
      }
      resolve(blob)
    }, 'image/png')
  })
}

/**
 * Load an `<img>` with crossOrigin set so the resulting bitmap is CORS-clean
 * and safe to draw onto the canvas.
 */
export function loadCorsImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = (err) =>
      reject(
        new ShareCardCompositionError(
          'canvas-tainted',
          `image load failed: ${err instanceof Event ? 'network/CORS' : String(err)}`,
        ),
      )
    img.src = src
  })
}
